import React from 'react';

// ── Color palette ──
const SSP = '#2563EB', SSPl = 'rgba(37,99,235,0.18)';
const DSP = '#F97316', DSPl = 'rgba(249,115,22,0.18)';
const BDR = '#16A34A', BDRl = 'rgba(22,163,74,0.18)';
const ERR = '#DC2626', ERRl = 'rgba(220,38,38,0.18)';
const MUT = '#6B7280';
const fIBM = "'IBM Plex Sans', sans-serif";
const fOut = "'Outfit', sans-serif";
const fMono = "'JetBrains Mono', monospace";

// ── Helpers ──
const Grid = ({ id = 'g' }) => (
  <defs>
    <pattern id={id} width="40" height="40" patternUnits="userSpaceOnUse">
      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(37,99,235,0.1)" strokeWidth="0.5" />
    </pattern>
  </defs>
);
const BG = ({ id = 'g' }) => <rect width="800" height="450" fill={`url(#${id})`} opacity="0.4" />;

const Node = ({ x, y, w, h, color, bg, rx = 10, cls, children }) => (
  <g className={cls}>
    <rect x={x} y={y} width={w} height={h} rx={rx} fill={bg} stroke={color} strokeWidth="1.8" />
    {children}
  </g>
);

const T = ({ x, y, s = 12, c = '#fff', a = 'middle', bold, mono, cls, children }) => (
  <text x={x} y={y} textAnchor={a} dominantBaseline="middle" fill={c} fontSize={s}
    fontWeight={bold ? '700' : '400'}
    fontFamily={mono ? fMono : bold ? fOut : fIBM}
    className={cls}>
    {children}
  </text>
);

// Flowing packets helper
const Pkts = ({ paths, color, dur = '2s', step = 0.7, n = 3 }) =>
  paths.flatMap((path, pi) =>
    Array.from({ length: n }, (_, i) => (
      <circle key={`${pi}-${i}`} r={4} fill={color}>
        <animateMotion path={path} dur={dur} begin={`${i * step}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.08;0.9;1"
          dur={dur} begin={`${i * step}s`} repeatCount="indefinite" />
      </circle>
    ))
  );

// Dropped packet — fades at ~45% of path
const Drop = ({ path, dur = '2.8s', begin = '0s' }) => (
  <circle r={4} fill={ERR}>
    <animateMotion path={path} dur={dur} begin={begin} repeatCount="indefinite" />
    <animate attributeName="opacity" values="0;0.9;0.9;0;0" keyTimes="0;0.08;0.42;0.52;1"
      dur={dur} begin={begin} repeatCount="indefinite" />
  </circle>
);

// ═══════════════════════════════════════════════
// SCENE 1 — The Programmatic Ecosystem
// ═══════════════════════════════════════════════
export const S1 = () => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />
    <T x={140} y={64} s={10} c={MUT} mono cls="label-appear">PUBLISHERS</T>
    <T x={400} y={64} s={10} c={MUT} mono cls="label-appear">SSP</T>
    <T x={660} y={64} s={10} c={MUT} mono cls="label-appear">DSP BIDDERS</T>

    {/* Connection lines */}
    {['M 215 125 L 312 205', 'M 215 222 L 312 225', 'M 215 319 L 312 245'].map((d, i) => (
      <line key={i} x1={d.split(' ')[1]} y1={d.split(' ')[2]} x2={d.split(' ')[4]} y2={d.split(' ')[5]}
        stroke={SSP} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.5"
        className={`line-appear${i > 0 ? '-' + (i + 1) : ''}`} />
    ))}
    {['M 490 205 L 585 125', 'M 490 225 L 585 222', 'M 490 245 L 585 319'].map((d, i) => (
      <line key={i} x1={d.split(' ')[1]} y1={d.split(' ')[2]} x2={d.split(' ')[4]} y2={d.split(' ')[5]}
        stroke={DSP} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.5"
        className={`line-appear${i > 0 ? '-' + (i + 1) : ''}`} />
    ))}

    {/* Publisher nodes */}
    {[['Website', 100], ['Mobile App', 197], ['Live Streaming TV', 294]].map(([lbl, y], i) => (
      <Node key={i} x={60} y={y} w={155} h={50} color={SSP} bg={SSPl} cls={`node-appear-${i + 1}`}>
        <T x={137} y={y + 25} s={11}>{lbl}</T>
      </Node>
    ))}

    {/* SSP node */}
    <Node x={310} y={165} w={180} h={120} color={SSP} bg={SSPl} rx={14} cls="node-appear-4 node-ssp">
      <T x={400} y={210} s={14} bold>Index Exchange</T>
      <T x={400} y={234} s={11} c="#93C5FD">SSP Marketplace</T>
      <rect x={340} y={252} width={120} height={20} rx={4} fill="rgba(37,99,235,0.3)" />
      <T x={400} y={262} s={9} c="#BFDBFE" mono>Ad Auction Engine</T>
    </Node>

    {/* DSP nodes */}
    {[['DSP Bidder 1', 100], ['DSP Bidder 2', 197], ['DSP Bidder 3', 294]].map(([lbl, y], i) => (
      <Node key={i} x={585} y={y} w={155} h={50} color={DSP} bg={DSPl} cls={`node-appear-${i + 5}`}>
        <T x={662} y={y + 25} s={11}>{lbl}</T>
      </Node>
    ))}

    {/* Packets */}
    <Pkts paths={['M 215 125 L 312 205', 'M 215 222 L 312 225', 'M 215 319 L 312 245']} color="#60A5FA" dur="1.9s" />
    <Pkts paths={['M 490 205 L 585 125', 'M 490 225 L 585 222', 'M 490 245 L 585 319']} color="#FB923C" dur="1.9s" />
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 2 — Traditional DSP Architecture
// ═══════════════════════════════════════════════
export const S2 = () => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />

    {/* Long dashed network lines */}
    <line x1="300" y1="215" x2="600" y2="95" stroke={SSP} strokeWidth="1.5" strokeDasharray="8,5" opacity="0.4" className="line-appear" />
    <line x1="300" y1="225" x2="620" y2="225" stroke={SSP} strokeWidth="1.5" strokeDasharray="8,5" opacity="0.4" className="line-appear-2" />
    <line x1="300" y1="235" x2="600" y2="355" stroke={SSP} strokeWidth="1.5" strokeDasharray="8,5" opacity="0.4" className="line-appear-3" />

    {/* SSP node */}
    <Node x={100} y={175} w={200} h={100} color={SSP} bg={SSPl} rx={14} cls="node-appear-1 node-ssp">
      <T x={200} y={218} s={13} bold>Index Exchange</T>
      <T x={200} y={240} s={11} c="#93C5FD">SSP</T>
    </Node>

    {/* External DSP nodes (far right) */}
    {[['External DSP A', 70], ['External DSP B', 200], ['External DSP C', 330]].map(([lbl, y], i) => (
      <Node key={i} x={620} y={y} w={155} h={50} color={DSP} bg={DSPl} cls={`node-appear-${i + 2}`}>
        <T x={697} y={y + 25} s={10}>{lbl}</T>
      </Node>
    ))}

    {/* Dropped packets (red, fade midway) */}
    {['M 300 215 C 400 160 480 110 560 105', 'M 300 225 C 400 225 490 225 560 225', 'M 300 235 C 400 290 490 340 560 345'].map((path, i) => (
      <g key={i}>
        <Drop path={path} dur="2.8s" begin={`${i * 0.9}s`} />
        <Drop path={path} dur="2.8s" begin={`${i * 0.9 + 1.4}s`} />
      </g>
    ))}

    {/* Successful packets (fewer) */}
    <Pkts paths={['M 300 225 L 620 225']} color="#F97316" dur="3.5s" step={1.8} n={1} />

    {/* DROPPED badges that flash */}
    {[{ x: 490, y: 128 }, { x: 490, y: 228 }, { x: 490, y: 328 }].map((pos, i) => (
      <g key={i}>
        <rect x={pos.x - 42} y={pos.y - 10} width="84" height="20" rx="4" fill={ERRl} stroke={ERR} strokeWidth="1">
          <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.2;0.35;0.55;0.65;1"
            dur="2.8s" begin={`${i * 0.9}s`} repeatCount="indefinite" />
        </rect>
        <T x={pos.x} y={pos.y} s={9} c={ERR} mono>
          <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.2;0.35;0.55;0.65;1"
            dur="2.8s" begin={`${i * 0.9}s`} repeatCount="indefinite" />
          DROPPED
        </T>
      </g>
    ))}

    {/* Latency label */}
    <rect x="310" y="390" width="155" height="36" rx="6" fill="rgba(220,38,38,0.12)" stroke={ERR} strokeWidth="1" className="label-appear" />
    <T x={387} y={408} s={11} c={ERR} bold cls="label-appear">Latency: &gt;100ms</T>

    <rect x="480" y="390" width="155" height="36" rx="6" fill="rgba(249,115,22,0.12)" stroke={DSP} strokeWidth="1" className="label-appear-2" />
    <T x={557} y={408} s={11} c={DSP} bold cls="label-appear-2">Traffic Shaping</T>

    <rect x="150" y="390" width="145" height="36" rx="6" fill={ERRl} stroke={ERR} strokeWidth="1" className="label-appear-3" />
    <T x={222} y={408} s={11} c={ERR} bold cls="label-appear-3">Dropped Bids</T>

    {/* Network distance indicator */}
    <T x={460} y={168} s={10} c={MUT} cls="label-appear-2">── Network travel ──</T>
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 3 — Traffic Shaping Problem
// ═══════════════════════════════════════════════
export const S3 = () => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />

    {/* Title labels */}
    <T x={120} y={64} s={10} c={MUT} mono cls="label-appear">INCOMING BIDS</T>
    <T x={400} y={64} s={10} c={MUT} mono cls="label-appear">TRAFFIC SHAPING GATE</T>
    <T x={680} y={64} s={10} c={MUT} mono cls="label-appear">PASSED BIDS</T>

    {/* Funnel shape */}
    <path d="M 260 100 L 500 155 L 500 295 L 260 350 Z"
      fill="rgba(37,99,235,0.08)" stroke={SSP} strokeWidth="1.5" strokeDasharray="8,5"
      className="node-appear-3" />
    <T x={380} y={210} s={12} bold cls="node-appear-4">Traffic Shaping</T>
    <T x={380} y={232} s={10} c="#93C5FD" cls="node-appear-4">Algorithm</T>

    {/* DSP box right */}
    <Node x={580} y={185} w={160} h={80} color={DSP} bg={DSPl} rx={12} cls="node-appear-5">
      <T x={660} y={225} s={11}>External DSP</T>
    </Node>

    {/* Incoming packets (many) going into funnel left side */}
    {[80, 120, 155, 185, 215, 245, 275, 310].map((y, i) => (
      <circle key={i} r={4} fill={i % 3 === 2 ? ERR : '#60A5FA'} className={`node-appear-${Math.min(i + 1, 8)}`}>
        <animateMotion path={`M 50 ${y} L 255 ${180 + (y - 150) * 0.4}`} dur={`${1.5 + i * 0.15}s`} begin={`${i * 0.3}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.1;0.85;1"
          dur={`${1.5 + i * 0.15}s`} begin={`${i * 0.3}s`} repeatCount="indefinite" />
      </circle>
    ))}

    {/* Passed packets (few) exiting right */}
    <Pkts paths={['M 500 200 L 580 215', 'M 500 225 L 580 225', 'M 500 250 L 580 235']} color={SSP} dur="2s" step={1} n={2} />

    {/* Dropped packets going down */}
    {['M 370 200 L 370 380', 'M 400 220 L 420 380', 'M 340 240 L 310 380'].map((path, i) => (
      <g key={i}>
        <Drop path={path} dur="2s" begin={`${i * 0.7}s`} />
      </g>
    ))}

    {/* Drop annotations */}
    <rect x="170" y="370" width="185" height="48" rx="8" fill={ERRl} stroke={ERR} strokeWidth="1" className="label-appear-3" />
    <T x={262} y={388} s={10} c={ERR} bold cls="label-appear-3">Random Drops</T>
    <T x={262} y={408} s={9} c="rgba(255,255,255,0.5)" cls="label-appear-3">Arbitrary filtering</T>

    <rect x="385" y="370" width="220" height="48" rx="8" fill="rgba(249,115,22,0.12)" stroke={DSP} strokeWidth="1" className="label-appear-4" />
    <T x={495} y={388} s={10} c={DSP} bold cls="label-appear-4">Conflict of Interest</T>
    <T x={495} y={408} s={9} c="rgba(255,255,255,0.5)" cls="label-appear-4">Low floor price drops</T>

    {/* X mark for dropped */}
    <T x={380} y={355} s={22} c={ERR} cls="label-appear-3">↓</T>
    <T x={280} y={340} s={14} c={ERR} cls="label-appear-3">Filtered out</T>
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 4 — Missed Opportunities
// ═══════════════════════════════════════════════
export const S4 = () => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />
    <T x={400} y={64} s={10} c={MUT} mono cls="label-appear">CAMPAIGNS NEVER GET TO BID</T>

    {/* Inventory sources left */}
    {[
      ['PMP Deal', 100, SSP, SSPl],
      ['Premium Publisher', 195, '#7C3AED', 'rgba(124,58,237,0.18)'],
      ['Live Stream', 290, DSP, DSPl],
    ].map(([lbl, y, col, bg], i) => (
      <Node key={i} x={60} y={y} w={170} h={50} color={col} bg={bg} cls={`node-appear-${i + 1}`}>
        <T x={145} y={y + 25} s={11}>{lbl}</T>
      </Node>
    ))}

    {/* Empty DSP box */}
    <Node x={520} y={165} w={200} h={120} color={MUT} bg="rgba(107,114,128,0.1)" rx={14} cls="node-appear-4">
      <T x={620} y={218} s={13} c="rgba(255,255,255,0.4)" bold>External DSP</T>
      <T x={620} y={242} s={10} c="rgba(255,255,255,0.25)">campaign selection</T>
    </Node>

    {/* Impressions flying PAST the DSP */}
    {[
      { path: 'M 232 125 C 380 120 480 110 780 80', color: SSP },
      { path: 'M 232 220 C 380 215 490 215 780 215', color: '#7C3AED' },
      { path: 'M 232 315 C 380 320 480 330 780 360', color: DSP },
    ].map(({ path, color }, pi) =>
      [0, 0.8, 1.6].map((d, i) => (
        <circle key={`${pi}-${i}`} r={4} fill={color}>
          <animateMotion path={path} dur="2.5s" begin={`${d}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;0.9;0.9;0" keyTimes="0;0.06;0.9;1"
            dur="2.5s" begin={`${d}s`} repeatCount="indefinite" />
        </circle>
      ))
    )}

    {/* Red X overlays */}
    {[{ x: 475, y: 140 }, { x: 475, y: 230 }, { x: 475, y: 330 }].map((p, i) => (
      <g key={i} className={`label-appear-${i + 2}`}>
        <circle cx={p.x} cy={p.y} r={16} fill={ERRl} stroke={ERR} strokeWidth="1.5" />
        <text x={p.x} y={p.y + 1} textAnchor="middle" dominantBaseline="middle"
          fill={ERR} fontSize="16" fontWeight="bold">✕</text>
      </g>
    ))}

    {/* Labels */}
    {[
      ['Missed Impressions', 370, ERR, 390],
      ['Dropped Bids', 390, ERR, 260],
      ['Lost Revenue', 390, ERR, 130],
    ].map(([lbl, y, c, x], i) => (
      <g key={i} className={`label-appear-${i + 2}`}>
        <rect x={x + 2} y={y - 14} width={160} height={28} rx="6" fill={ERRl} stroke={ERR} strokeWidth="1" />
        <T x={x + 82} y={y} s={11} c={c} bold>{lbl}</T>
      </g>
    ))}
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 5 — DSP Running Inside SSP
// ═══════════════════════════════════════════════
export const S6 = ({ bedrockLogo }) => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />

    {/* Publisher on left */}
    <Node x={30} y={180} w={150} h={90} color={SSP} bg={SSPl} rx={12} cls="node-appear-1">
      <T x={105} y={215} s={12}>Publishers</T>
      <T x={105} y={237} s={10} c="#93C5FD">All Inventory</T>
    </Node>

    {/* Short arrow line */}
    <line x1="182" y1="225" x2="250" y2="225" stroke={SSP} strokeWidth="2" className="line-appear" />
    <polygon points="248,218 260,225 248,232" fill={SSP} className="line-appear" />

    {/* SSP wrapper */}
    <Node x={260} y={100} w={500} h={250} color={SSP} bg={SSPl} rx={18} cls="node-appear-2 node-ssp">
      <T x={510} y={133} s={13} bold>Index Exchange SSP</T>
      <T x={510} y={155} s={10} c="#93C5FD">Infrastructure</T>
    </Node>

    {/* Bedrock container inside SSP */}
    <g className="container-drop node-bedrock">
      <rect x={340} y={178} width={340} height={138} rx={12}
        fill={BDRl} stroke={BDR} strokeWidth="2.5" />
      <T x={510} y={220} s={13} bold c="#4ADE80">Bedrock DSP Container</T>
      {bedrockLogo ? (
        <image href={bedrockLogo} x={415} y={232} width={190} height={40}
          style={{ filter: 'brightness(0) invert(1)' }} preserveAspectRatio="xMidYMid meet" />
      ) : (
        <T x={510} y={254} s={11} bold c="#86EFAC">bedrockplatform</T>
      )}
      <T x={510} y={278} s={9} c="#86EFAC" mono>containerized · inside SSP</T>
    </g>

    {/* Fast packets (very short path = instant) */}
    <Pkts paths={['M 182 225 L 338 225']} color="#60A5FA" dur="0.9s" step={0.3} n={4} />

    {/* Inside-SSP packets to Bedrock (even faster) */}
    <Pkts paths={['M 338 225 L 338 240']} color={BDR} dur="0.4s" step={0.15} n={3} />

    {/* Labels */}
    <g className="label-appear-3">
      <rect x={30} y={290} width={150} height={48} rx="8" fill={BDRl} stroke={BDR} strokeWidth="1" />
      <T x={105} y={308} s={10} c={BDR} bold>0 Network Hops</T>
      <T x={105} y={326} s={9} c="rgba(255,255,255,0.5)">Instant access</T>
    </g>
    <g className="label-appear-4">
      <rect x={30} y={355} width={150} height={38} rx="8" fill={BDRl} stroke={BDR} strokeWidth="1" />
      <T x={105} y={374} s={11} c="#4ADE80" bold>&lt;1ms response</T>
    </g>
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 7 — 100% Deal Visibility
// ═══════════════════════════════════════════════
export const S7 = () => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />
    <T x={400} y={58} s={10} c={MUT} mono cls="label-appear">DEAL VISIBILITY COMPARISON</T>

    {/* Left panel — Traditional */}
    <Node x={70} y={88} w={290} h={290} color={MUT} bg="rgba(107,114,128,0.1)" rx={14} cls="node-appear-1">
      <T x={215} y={118} s={13} bold c="rgba(255,255,255,0.7)">Traditional DSP</T>
      <T x={215} y={140} s={10} c="rgba(255,255,255,0.4)">External Network</T>
    </Node>

    {/* Left bar container */}
    <rect x={120} y={165} width={190} height={170} rx="8" fill="rgba(0,0,0,0.3)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
    {/* Left bar fill — ~62% */}
    <rect x={120} y={272} width={190} height={0} rx="4" fill={DSP}>
      <animate attributeName="height" from="0" to="106" dur="1.5s" begin="0.4s" fill="freeze" />
      <animate attributeName="y" from="335" to="229" dur="1.5s" begin="0.4s" fill="freeze" />
    </rect>
    <T x={215} y={358} s={20} bold c={DSP} cls="label-appear-2">~60%</T>
    <T x={215} y={400} s={20} c="rgba(255,255,255,0.4)" cls="label-appear-2">visibility</T>

    {/* Traffic shaped badge on left */}
    <g className="label-appear-2">
      <rect x={100} y={158} width={130} height={22} rx="5" fill={ERRl} stroke={ERR} strokeWidth="1" />
      <T x={165} y={169} s={9} c={ERR} bold>Traffic Shaped</T>
    </g>

    {/* Right panel — Bedrock */}
    <Node x={440} y={88} w={290} h={290} color={BDR} bg={BDRl} rx={14} cls="node-appear-2 node-bedrock">
      <T x={585} y={118} s={13} bold c="#4ADE80">Bedrock Container</T>
      <T x={585} y={140} s={10} c="#86EFAC">Inside Index Exchange SSP</T>
    </Node>

    {/* Right bar container */}
    <rect x={490} y={165} width={190} height={170} rx="8" fill="rgba(0,0,0,0.3)" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
    {/* Right bar fill — 100% */}
    <rect x={490} y={335} width={190} height={0} rx="4" fill={BDR}>
      <animate attributeName="height" from="0" to="170" dur="1.8s" begin="0.6s" fill="freeze" />
      <animate attributeName="y" from="335" to="165" dur="1.8s" begin="0.6s" fill="freeze" />
    </rect>
    <T x={585} y={358} s={20} bold c="#4ADE80" cls="label-appear-3">100%</T>
    <T x={585} y={400} s={20} c="#86EFAC" cls="label-appear-3">impressions evaluated</T>

    {/* Checkmark */}
    <g className="label-appear-4">
      <circle cx={648} cy={156} r={16} fill={BDR} />
      <text x={648} y={157} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="14">✓</text>
    </g>

    {/* VS divider */}
    <T x={400} y={230} s={22} bold c="rgba(255,255,255,0.2)" cls="label-appear">VS</T>
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 8 — Ultra-Low Latency
// ═══════════════════════════════════════════════
export const S8 = () => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />
    <T x={400} y={58} s={10} c={MUT} mono cls="label-appear">BID RESPONSE LATENCY COMPARISON</T>

    {/* Row 1 — External DSP */}
    <Node x={50} y={120} w={200} h={80} color={MUT} bg="rgba(107,114,128,0.12)" rx={12} cls="node-appear-1">
      <T x={150} y={152} s={12} bold c="rgba(255,255,255,0.7)">External DSP</T>
      <T x={150} y={174} s={10} c={MUT}>Outside SSP</T>
    </Node>

    {/* Long red latency bar */}
    <rect x={265} y={143} width={0} height={34} rx="5" fill="rgba(220,38,38,0.2)" stroke={ERR} strokeWidth="1">
      <animate attributeName="width" from="0" to="440" dur="1.8s" begin="0.3s" fill="freeze" />
    </rect>
    <rect x={265} y={143} width={0} height={34} rx="5" fill={ERR} opacity="0.7">
      <animate attributeName="width" from="0" to="440" dur="1.8s" begin="0.3s" fill="freeze" />
    </rect>
    <T x={490} y={175} s={22} bold c={ERR} cls="label-appear-2">&gt;100ms</T>

    {/* Stopwatch icon area */}
    <g className="label-appear-2">
      <circle cx={730} cy={160} r={28} fill="rgba(220,38,38,0.15)" stroke={ERR} strokeWidth="1.5" />
      <T x={730} y={160} s={11} c={ERR} bold mono>SLOW</T>
    </g>

    {/* Row 2 — Bedrock Container */}
    <Node x={50} y={268} w={200} h={80} color={BDR} bg={BDRl} rx={12} cls="node-appear-3 node-bedrock">
      <T x={150} y={300} s={12} bold c="#4ADE80">Bedrock DSP</T>
      <T x={150} y={322} s={10} c="#86EFAC">Inside SSP</T>
    </Node>

    {/* Short green bar */}
    <rect x={265} y={291} width={0} height={34} rx="5" fill="rgba(22,163,74,0.2)" stroke={BDR} strokeWidth="1">
      <animate attributeName="width" from="0" to="8" dur="0.2s" begin="0.8s" fill="freeze" />
    </rect>
    <rect x={265} y={291} width={0} height={34} rx="5" fill={BDR} opacity="0.8">
      <animate attributeName="width" from="0" to="8" dur="0.2s" begin="0.8s" fill="freeze" />
    </rect>
    <T x={370} y={323} s={22} bold c="#4ADE80" cls="label-appear-3">&lt;1ms</T>

    {/* Lightning bolt */}
    <g className="label-appear-4">
      <circle cx={730} cy={308} r={28} fill={BDRl} stroke={BDR} strokeWidth="1.5" />
      <text x={730} y={309} textAnchor="middle" dominantBaseline="middle" fill="#4ADE80" fontSize="20">⚡</text>
    </g>

    {/* Scale indicator */}
    <rect x={265} y={220} width={440} height={1} fill="rgba(255,255,255,0.1)" />
    <T x={265} y={212} s={9} c={MUT} a="start" mono cls="label-appear">0ms</T>
    <T x={705} y={212} s={9} c={MUT} a="end" mono cls="label-appear">&gt;100ms</T>

    {/* Improvement label */}
    <g className="label-appear-5">
      <rect x={240} y={380} width={320} height={40} rx="10" fill={BDRl} stroke={BDR} strokeWidth="1.5" />
      <T x={400} y={400} s={13} bold c="#4ADE80">100× Faster Bid Response</T>
    </g>
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 9 — Live Streaming Advantage
// ═══════════════════════════════════════════════
export const S9 = () => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />
    <T x={400} y={58} s={10} c={MUT} mono cls="label-appear">LIVE STREAMING — CONCURRENT AUCTIONS</T>

    {/* Live stream TV icons */}
    {[140, 400, 660].map((x, i) => (
      <g key={i} className={`node-appear-${i + 1}`}>
        <rect x={x - 70} y={78} width={140} height={82} rx={8}
          fill="rgba(37,99,235,0.12)" stroke={SSP} strokeWidth="1.5" />
        <rect x={x - 55} y={88} width={110} height={58} rx={4} fill="#0A0A0A" />
        <T x={x} y={117} s={10} c={ERR} bold>● LIVE</T>
        <T x={x} y={135} s={9} c="rgba(255,255,255,0.4)">100K viewers</T>
        <line x1={x} y1={160} x2={x} y2={190} stroke={SSP} strokeWidth="1.5" strokeDasharray="4,3" />
      </g>
    ))}

    {/* Converge line */}
    <path d="M 140 190 C 140 220 400 220 400 220 C 400 220 660 220 660 190"
      fill="none" stroke={SSP} strokeWidth="1.5" strokeDasharray="5,4" opacity="0.5" className="line-appear-2" />

    {/* Split point */}
    <circle cx={280} cy={220} r={6} fill={SSP} className="node-appear-3" />
    <circle cx={520} cy={220} r={6} fill={SSP} className="node-appear-3" />
    <line x1="280" y1="220" x2="280" y2="260" stroke={MUT} strokeWidth="1.5" className="line-appear-3" />
    <line x1="520" y1="220" x2="520" y2="260" stroke={MUT} strokeWidth="1.5" className="line-appear-3" />

    {/* External DSP path (left) */}
    <Node x={130} y={260} w={300} h={80} color={MUT} bg="rgba(107,114,128,0.1)" rx={10} cls="node-appear-4">
      <T x={280} y={292} s={12} bold c="rgba(255,255,255,0.6)">External DSP</T>
      <T x={280} y={314} s={10} c={MUT}>Outside SSP</T>
    </Node>

    {/* Timeout indicators */}
    {[{ x: 175, y: 366 }, { x: 260, y: 380 }, { x: 345, y: 366 }].map((p, i) => (
      <g key={i} className={`label-appear-${i + 2}`}>
        <rect x={p.x - 42} y={p.y - 14} width={84} height={26} rx="6" fill={ERRl} stroke={ERR} strokeWidth="1" />
        <T x={p.x} y={p.y} s={10} c={ERR} bold>TIMEOUT ✕</T>
      </g>
    ))}

    {/* Bedrock Container path (right) */}
    <Node x={370} y={260} w={300} h={80} color={BDR} bg={BDRl} rx={10} cls="node-appear-5 node-bedrock">
      <T x={520} y={292} s={12} bold c="#4ADE80">Bedrock Container</T>
      <T x={520} y={314} s={10} c="#86EFAC">Inside Index Exchange</T>
    </Node>

    {/* Win indicators */}
    {[{ x: 415, y: 366 }, { x: 520, y: 380 }, { x: 625, y: 366 }].map((p, i) => (
      <g key={i} className={`label-appear-${i + 3}`}>
        <rect x={p.x - 32} y={p.y - 14} width={64} height={26} rx="6" fill={BDRl} stroke={BDR} strokeWidth="1" />
        <T x={p.x} y={p.y} s={10} c="#4ADE80" bold>WIN ✓</T>
      </g>
    ))}

    {/* Packet flows to each */}
    <Pkts paths={['M 280 220 L 280 260']} color={ERR} dur="1.2s" step={0.4} n={3} />
    <Pkts paths={['M 520 220 L 520 260']} color={BDR} dur="0.7s" step={0.25} n={4} />
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 10 — Higher Win Rates
// ═══════════════════════════════════════════════
export const S10 = () => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />
    <T x={400} y={58} s={10} c={MUT} mono cls="label-appear">CAMPAIGN PERFORMANCE METRICS</T>

    {/* Three metric cards */}
    {[
      { x: 80, label: 'Win Rate', arrow: '↑', color: BDR, from: '22%', to: '78%', grows: true },
      { x: 305, label: 'Dropped Bids', arrow: '↓', color: '#4ADE80', from: '65%', to: '8%', grows: false },
      { x: 530, label: 'Timeouts', arrow: '↓', color: '#4ADE80', from: '42%', to: '3%', grows: false },
    ].map(({ x, label, arrow, color, from, to, grows }, i) => (
      <g key={i} className={`node-appear-${i + 1}`}>
        <rect x={x} y={88} width={195} height={300} rx={14} fill="rgba(18,18,18,0.8)" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" />

        <T x={x + 97} y={116} s={13} bold>{label}</T>

        {/* Arrow indicator */}
        <g>
          <rect x={x + 72} y={130} width={50} height={24} rx="6"
            fill={grows ? BDRl : ERRl} />
          <T x={x + 97} y={142} s={14} bold c={grows ? BDR : ERR}>{arrow}</T>
        </g>

        {/* Bar background */}
        <rect x={x + 40} y={168} width={115} height={170} rx="6" fill="rgba(0,0,0,0.3)" />

        {/* "Before" bar (always static, faded) */}
        <rect x={x + 40} y={grows ? 293 : 168} width={115}
          height={grows ? 45 : 115} rx="4" fill="rgba(255,255,255,0.08)" />

        {/* "After" animated bar */}
        <rect x={x + 40} y={338} width={115} height={0} rx="4" fill={color} opacity="0.8">
          <animate attributeName="height" from="0"
            to={grows ? '132' : '14'} dur="1.8s" begin={`${0.4 + i * 0.2}s`} fill="freeze" />
          <animate attributeName="y" from="338"
            to={grows ? '206' : '324'} dur="1.8s" begin={`${0.4 + i * 0.2}s`} fill="freeze" />
        </rect>

        {/* Before label */}
        <T x={x + 160} y={grows ? 308 : 228} s={9} c="rgba(255,255,255,0.3)" a="end">{from}</T>

        {/* After value */}
        <T x={x + 97} y={370} s={22} bold c={color}>{to}</T>

        {/* "After Bedrock" label */}
        <T x={x + 97} y={395} s={9} c="rgba(255,255,255,0.4)" cls={`label-appear-${i + 3}`}>with Bedrock</T>
      </g>
    ))}

    <T x={400} y={430} s={11} c="rgba(255,255,255,0.35)" cls="label-appear-4">
      Full deal visibility + &lt;1ms latency = dramatically better performance
    </T>
  </svg>
);

// ═══════════════════════════════════════════════
// SCENE 11 — Stable Campaign Delivery
// ═══════════════════════════════════════════════
export const S11 = () => {
  const erratic = "M 80 240 L 120 200 L 160 280 L 200 185 L 240 260 L 280 170 L 320 300 L 360 220 L 400 270 L 440 185 L 480 310 L 520 230 L 560 260 L 600 195 L 640 280 L 680 230 L 720 250";
  const smooth = "M 80 260 L 200 252 L 320 248 L 440 244 L 560 242 L 680 240 L 720 238";
  const pathLen = 700;
  return (
    <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
      <Grid /><BG />
      <T x={400} y={58} s={10} c={MUT} mono cls="label-appear">IMPRESSION DELIVERY OVER TIME</T>

      {/* Chart axes */}
      <line x1="80" y1="340" x2="730" y2="340" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
      <line x1="80" y1="100" x2="80" y2="340" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />

      {/* Y-axis labels */}
      {[['High', 120], ['Med', 220], ['Low', 320]].map(([l, y]) => (
        <T key={l} x={70} y={y} s={9} c={MUT} a="end" mono>{l}</T>
      ))}
      <T x={400} y={370} s={9} c={MUT} mono cls="label-appear">Time →</T>
      <T x={45} y={240} s={9} c={MUT} mono cls="label-appear">Delivery</T>

      {/* Grid lines */}
      {[120, 220, 320].map(y => (
        <line key={y} x1="80" y1={y} x2="730" y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
      ))}

      {/* Erratic line (traditional DSP) */}
      <path d={erratic} fill="none" stroke={DSP} strokeWidth="2.5" opacity="0.7"
        strokeDasharray={pathLen} strokeDashoffset={pathLen} className="line-draw" />

      {/* Smooth line (Bedrock) */}
      <path d={smooth} fill="none" stroke={BDR} strokeWidth="3" opacity="0.9"
        strokeDasharray="700" strokeDashoffset="700" className="line-draw"
        style={{ animationDelay: '0.5s' }} />

      {/* Legend */}
      <g className="label-appear-3">
        <line x1="460" y1="400" x2="500" y2="400" stroke={DSP} strokeWidth="2.5" />
        <T x={560} y={400} s={10} c={DSP}>Traditional DSP (erratic)</T>
      </g>
      <g className="label-appear-4">
        <line x1="460" y1="422" x2="500" y2="422" stroke={BDR} strokeWidth="3" />
        <T x={575} y={422} s={10} c="#4ADE80">Bedrock Container (stable)</T>
      </g>

      {/* Annotation */}
      <g className="label-appear-5">
        <rect x={570} y={215} width={155} height={42} rx="8" fill={BDRl} stroke={BDR} strokeWidth="1" />
        <T x={647} y={232} s={10} bold c="#4ADE80">Steady Delivery</T>
        <T x={647} y={250} s={9} c="#86EFAC">Balanced pacing</T>
      </g>
    </svg>
  );
};

// ═══════════════════════════════════════════════
// SCENE 12 — Final Message
// ═══════════════════════════════════════════════
export const S12 = ({ indexLogo, bedrockLogo }) => (
  <svg viewBox="0 0 800 450" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
    <Grid /><BG />

    {/* Background glow */}
    <ellipse cx="400" cy="225" rx="300" ry="200" fill="rgba(22,163,74,0.04)" />

    {/* Logos at top */}
    <g className="node-appear-1">
      <rect x={90} y={50} width={200} height={60} rx={10} fill="white" />
      {bedrockLogo
        ? <image href={bedrockLogo} x={95} y={55} width={190} height={50} preserveAspectRatio="xMidYMid meet" />
        : <text x={190} y={85} textAnchor="middle" dominantBaseline="middle" fill="#000" fontSize="14" fontWeight="700" fontFamily={fOut}>bedrockplatform</text>
      }
    </g>

    <T x={400} y={82} s={18} c="rgba(255,255,255,0.25)" cls="node-appear-2">×</T>

    <g className="node-appear-2">
      <rect x={510} y={50} width={200} height={60} rx={10} fill="white" />
      {indexLogo
        ? <image href={indexLogo} x={515} y={55} width={190} height={50} preserveAspectRatio="xMidYMid meet" />
        : <text x={610} y={85} textAnchor="middle" dominantBaseline="middle" fill="#064E3B" fontSize="16" fontWeight="700" fontFamily={fOut}>Index Exchange</text>
      }
    </g>

    <T x={400} y={144} s={14} c="rgba(255,255,255,0.5)" cls="node-appear-3">
      Containerized inside Index Exchange SSP Infrastructure
    </T>

    {/* Divider */}
    <line x1="200" y1="175" x2="600" y2="175" stroke="rgba(255,255,255,0.1)" strokeWidth="1" className="line-appear" />

    {/* Bullet points */}
    {[
      ['100% deal visibility', BDR],
      ['<1ms bid latency', '#4ADE80'],
      ['Higher win rates', BDR],
      ['Better live streaming performance', '#4ADE80'],
      ['Stable campaign delivery', BDR],
    ].map(([text, color], i) => (
      <g key={i} className={`bullet-${i + 1}`}>
        <circle cx={224} cy={200 + i * 42} r={12} fill={color} opacity="0.9" />
        <text x={224} y={201 + i * 42} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="12">✓</text>
        <text x={248} y={200 + i * 42} textAnchor="start" dominantBaseline="middle"
          fill="#fff" fontSize="15" fontFamily={fIBM} fontWeight="600">{text}</text>
      </g>
    ))}

    {/* Animated flowing impressions in background */}
    {['M -50 120 L 850 120', 'M -50 250 L 850 250', 'M -50 380 L 850 380'].map((path, pi) =>
      [0, 1.2, 2.4, 3.6].map((d, i) => (
        <circle key={`${pi}-${i}`} r={3} fill={pi === 1 ? BDR : SSP} opacity="0.2">
          <animateMotion path={path} dur="4s" begin={`${d}s`} repeatCount="indefinite" />
        </circle>
      ))
    )}
  </svg>
);

export const ALL_SCENES = [S1, S2, S3, S4, S6, S7, S8, S9, S10, S11, S12];
